import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../services/pocketbase';
import {
  keepAlive,
  onReauthChange,
  completeReauth,
  abandonReauth,
  currentEmail,
} from '../services/session';

const AuthContext = createContext(null);

// How often an open app checks whether its token needs renewing.
const KEEP_ALIVE_INTERVAL_MS = 30 * 60 * 1000;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined); // undefined = loading
  const [reauthNeeded, setReauthNeeded] = useState(false);

  useEffect(() => {
    // Initialise from PocketBase's persisted auth store
    setSession(pb.authStore.isValid ? pb.authStore : null);

    // Listen for auth store changes (login, logout, token refresh)
    const unsubscribe = pb.authStore.onChange(() => {
      setSession(pb.authStore.isValid ? pb.authStore : null);
    });

    return () => unsubscribe();
  }, []);

  // Renew the token before it expires: an app left open for days (a tab, or
  // the PWA in the Dock) otherwise wakes up with a dead session.
  useEffect(() => {
    keepAlive();
    const onVisible = () => {
      if (document.visibilityState === 'visible') keepAlive();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', keepAlive);
    const timer = setInterval(keepAlive, KEEP_ALIVE_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', keepAlive);
      clearInterval(timer);
    };
  }, []);

  useEffect(() => onReauthChange(setReauthNeeded), []);

  const signIn = async (email, password) => {
    try {
      await pb.collection('users').authWithPassword(email, password);
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const signOut = () => {
    abandonReauth();
    pb.authStore.clear();
    // Drop cached personal data and any queued writes belonging to this user.
    import('../services/api')
      .then((api) => api.clearOfflineData())
      .catch(() => {});
  };

  const getAccessToken = () => {
    return pb.authStore.token || null;
  };

  return (
    <AuthContext.Provider
      value={{ session, loading: session === undefined, signIn, signOut, getAccessToken }}
    >
      {children}
      {reauthNeeded && <ReauthDialog signIn={signIn} signOut={signOut} />}
    </AuthContext.Provider>
  );
}

/**
 * Sign-in dialog shown over the current page when the session has expired.
 * The page underneath stays mounted, so the form the user was sending keeps
 * its content, and the call that failed is replayed once they are back in.
 */
function ReauthDialog({ signIn, signOut }) {
  const { t } = useTranslation();
  // Same account only: replaying a call under someone else's name would be wrong.
  const [knownEmail] = useState(currentEmail);
  const [email, setEmail] = useState(knownEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await signIn(email, password);
    setLoading(false);
    if (err) {
      setError(t('auth.loginError'));
      return;
    }
    completeReauth();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 23, 42, 0.55)',
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          padding: 24,
          borderRadius: 'var(--radius, 8px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: 400,
        }}
      >
        <h2 id="reauth-title" style={{ marginBottom: 8 }}>{t('auth.reauthTitle')}</h2>
        <p style={{ marginBottom: 16, color: 'var(--color-text-light, #64748b)' }}>
          {t('auth.reauthText')}
        </p>

        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="reauth-email" style={{ display: 'block', marginBottom: 4 }}>
            {t('auth.email')}
          </label>
          <input
            id="reauth-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            readOnly={Boolean(knownEmail)}
            required
            autoComplete="email"
            style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label htmlFor="reauth-password" style={{ display: 'block', marginBottom: 4 }}>
            {t('auth.password')}
          </label>
          <input
            id="reauth-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            autoComplete="current-password"
            style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={signOut} disabled={loading}>
            {t('auth.reauthSignOut')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('auth.loggingIn') : t('auth.reauthButton')}
          </button>
        </div>
      </form>
    </div>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
